// src/utils/id.ts

/**
 * NJDC ID generator
 * - Projects: P001, P002, ...
 * - Everything else: keeps a simple unique-ish ID (prefix + random)
 *
 * NOTE: Project IDs are derived from what's already in localStorage key "njdc_projects".
 */

function safeJsonParse<T>(raw: string | null, fallback: T): T {
  try {
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

function readExistingProjectIds(): string[] {
  // DataContext persists raw projects under "njdc_projects"
  if (typeof window === "undefined") return [];
  const rows = safeJsonParse<any[]>(window.localStorage.getItem("njdc_projects"), []);
  const ids: string[] = [];
  for (let i = 0; i < rows.length; i++) {
    const id = rows[i]?.id;
    if (typeof id === "string" && id.trim()) ids.push(id.trim());
  }
  return ids;
}

function extractProjectNumber(id: string): number | null {
  const s = String(id || "").trim();

  // Accept common legacy formats + new format:
  // P001, P-001, PROJ-12345, PROJ_123, PROJmk1n...186337 (ends with digits)
  // We only consider it if it looks like a project-ish prefix.
  const upper = s.toUpperCase();

  const looksLikeProject =
    upper.startsWith("P") || upper.startsWith("PROJ");

  if (!looksLikeProject) return null;

  // Prefer direct P### match
  let m = s.match(/^[Pp]-?(\d+)$/);
  if (m && m[1]) return Number(m[1]);

  // Else: take trailing digits if present
  m = s.match(/(\d+)\s*$/);
  if (m && m[1]) return Number(m[1]);

  return null;
}

function padN(n: number, width: number) {
  const raw = String(Math.max(0, Math.floor(n)));
  if (raw.length >= width) return raw;
  return "0".repeat(width - raw.length) + raw;
}

function nextProjectId(): string {
  const existing = readExistingProjectIds();

  let maxN = 0;
  for (let i = 0; i < existing.length; i++) {
    const n = extractProjectNumber(existing[i]);
    if (typeof n === "number" && Number.isFinite(n)) {
      if (n > maxN) maxN = n;
    }
  }

  const next = maxN + 1;
  return `P${padN(next, 3)}`; // P001, P002...
}

export function generateId(prefix: string) {
  // Projects: enforce P###
  if (String(prefix).toUpperCase() === "PROJ") {
    return nextProjectId();
  }

  // Default fallback for other entities (internal IDs)
  // Example: EXP-483920
  const rand = Math.floor(Math.random() * 900000 + 100000); // 6 digits
  return `${prefix}-${rand}`;
}
